import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import SearchableSelect from '@/components/common/SearchableSelect';
import type { MaterialCategoryDto } from '@/types/materials';

function buildSchema(t: (key: string) => string) {
  return z.object({
    name: z.string().min(1, t('form.validation.nameRequired')),
    parentCategoryId: z.string().nullable(),
    type: z.enum(['RM', 'SFG', 'FG', '']),
    status: z.enum(['Active', 'Inactive']),
  });
}

export type CategoryFormValues = z.infer<ReturnType<typeof buildSchema>>;

const DEFAULTS: CategoryFormValues = { name: '', parentCategoryId: null, type: '', status: 'Active' };

interface CategoryFormDialogProps {
  open: boolean;
  category: MaterialCategoryDto | null;
  categories: MaterialCategoryDto[];
  onClose: () => void;
  onSubmit: (values: CategoryFormValues) => void;
  loading?: boolean;
}

export default function CategoryFormDialog({ open, category, categories, onClose, onSubmit, loading }: CategoryFormDialogProps) {
  const { t } = useTranslation('categoryMaster');
  const schema = buildSchema(t);

  const { control, handleSubmit, reset } = useForm<CategoryFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) {
      reset(
        category
          ? {
              name: category.name, parentCategoryId: category.parentCategoryId,
              type: category.type ?? '', status: category.status as 'Active' | 'Inactive',
            }
          : DEFAULTS,
      );
    }
  }, [open, category, reset]);

  const selectableParents = categories.filter((c) => c.id !== category?.id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{category ? category.name : t('addCategory')}</DialogTitle>
      <DialogContent>
        <GridContainer spacing={2} columns={1}>
          <GridItem xs={1}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.name')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="parentCategoryId"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  label={t('form.parentCategory')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                  options={[{ value: '', label: t('form.noParent') }, ...selectableParents.map((c) => ({ value: c.id, label: c.name }))]}
                />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="type"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field} select label={t('form.type')} fullWidth
                  error={Boolean(fieldState.error)} helperText={fieldState.error?.message ?? t('form.typeHint')}
                >
                  <MenuItem value="">{t('form.typeNone')}</MenuItem>
                  <MenuItem value="RM">{t('form.typeRm')}</MenuItem>
                  <MenuItem value="SFG">{t('form.typeSfg')}</MenuItem>
                  <MenuItem value="FG">{t('form.typeFg')}</MenuItem>
                </TextField>
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label={t('form.status')} fullWidth>
                  <MenuItem value="Active">{t('form.statusActive')}</MenuItem>
                  <MenuItem value="Inactive">{t('form.statusInactive')}</MenuItem>
                </TextField>
              )}
            />
          </GridItem>
        </GridContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('discardChanges')}</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          {t('saveCategory')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
